import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { ArrowRight } from 'lucide-react';
import { PRODUCTS } from '../data/productData';

const Products = () => {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const navigate = useNavigate();

  const products = PRODUCTS;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="py-20 bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-dark mb-4">
            Our <span className="text-primary">Products</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Scalable Digital Products Designed for Modern Businesses
          </p>
        </motion.div>

        {/* Product Slider */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative px-4 sm:px-0"
        >
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={32}
            loop={true}
            observer={true}
            observeParents={true}
            watchSlidesProgress={true}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            navigation={{
              nextEl: '.swiper-button-next-products',
              prevEl: '.swiper-button-prev-products',
            }}
            pagination={{
              el: '.swiper-pagination-products',
              clickable: true,
            }}
            breakpoints={{
              320: {
                slidesPerView: 1,
                spaceBetween: 20,
              },
              640: {
                slidesPerView: 1,
                spaceBetween: 24,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 28,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 32,
              },
              1280: {
                slidesPerView: 4,
                spaceBetween: 32,
              },
            }}
            className="pb-20"
          >
            {products.map((product) => (
              <SwiperSlide key={product.id}>
                <motion.div
                  onHoverStart={() => setHoveredProduct(product.id)}
                  onHoverEnd={() => setHoveredProduct(null)}
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 h-full flex flex-col border border-gray-100"
                >
                  {/* Product Image Section */}
                  <div
                    className={`relative h-60 bg-gradient-to-br ${product.color} overflow-hidden flex items-center justify-center`}
                  >
                    {product.image ? (
                      <motion.img
                        src={product.image}
                        alt={product.name}
                        animate={
                          hoveredProduct === product.id
                            ? { scale: 1.1 }
                            : { scale: 1 }
                        }
                        transition={{ duration: 0.3 }}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <motion.div
                        animate={
                          hoveredProduct === product.id
                            ? { scale: 1.1 }
                            : { scale: 1 }
                        }
                        transition={{ duration: 0.3 }}
                        className="text-6xl"
                      >
                        {product.icon}
                      </motion.div>
                    )}
                  </div>

                  {/* Product Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-dark mb-1">
                        {product.name}
                      </h3>
                      <p className="text-sm font-semibold text-primary mb-3">
                        {product.tagline}
                      </p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {product.description}
                      </p>
                    </div>

                    {/* Learn More Button */}
                    <motion.button
                      whileHover={{ gap: '12px' }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate(`/products/${product.slug}`)}
                      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm w-fit mt-auto ${hoveredProduct === product.id
                        ? 'bg-secondary text-white'
                        : 'bg-primary/10 text-primary hover:bg-primary/20'
                        }`}
                    >
                      Learn More <ArrowRight size={16} />
                    </motion.button>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Navigation Arrows */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="swiper-button-prev-products absolute left-0 top-1/3 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-soft hover:shadow-soft-lg transition-all duration-200 flex items-center justify-center text-primary hover:text-secondary hover:bg-light -ml-6 md:-ml-8"
            aria-label="Previous product"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="swiper-button-next-products absolute right-0 top-1/3 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-soft hover:shadow-soft-lg transition-all duration-200 flex items-center justify-center text-primary hover:text-secondary hover:bg-light -mr-6 md:-mr-8"
            aria-label="Next product"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </motion.button>

          {/* Custom Pagination Dots */}
          <div className="swiper-pagination-products absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-3 justify-center" />
        </motion.div>
      </div>

      {/* Custom Swiper Pagination Styles */}
      <style>{`
      .swiper-pagination-products {
  position: absolute !important;
  left: 50% !important;
  transform: translateX(-50%);
  bottom: 10px;
}

        .swiper-pagination-products .swiper-pagination-bullet {
          width: 10px;
          height: 10px;
          background-color: #d1d5db;
          opacity: 1;
          transition: all 0.3s ease;
        }

        .swiper-pagination-products .swiper-pagination-bullet-active {
          width: 28px;
          background-color: #E53935;
          border-radius: 6px;
        }

        .swiper-pagination-products .swiper-pagination-bullet:hover {
          background-color: #E53935;
        }

        @media (max-width: 768px) {
          .swiper-button-prev-products,
          .swiper-button-next-products {
            width: 40px;
            height: 40px;
          }
        }
      `}</style>
    </section>
  );
};

export default Products;
